import {
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  Card,
  CardContent,
  CardHeader,
  Box,
  Tooltip,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import React from "react";
import { useTheme } from "@mui/material/styles";
import ApexChart from "react-apexcharts";
import { styled } from "@mui/material/styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SigneeRegisterInfo from "../popover/SigneeRegisterInfo";
import { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

const Chart = styled(ApexChart)``;

const getChartOptions = (theme, mobileSize) => {
  return {
    chart: {
      background: "transparent",
      stacked: false,
      toolbar: {
        show: false,
      },
    },
    colors: ["#e8cc44"],
    dataLabels: {
      enabled: false,
    },
    fill: {
      opacity: 1,
      type: "solid",
    },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 2,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    legend: {
      show: false,
    },
    plotOptions: {
      bar: {
        columnWidth: mobileSize ? "15rem" : "25rem",
      },
    },
    stroke: {
      colors: ["transparent"],
      show: true,
      width: 2,
    },
    theme: {
      mode: theme.palette.mode,
    },
    xaxis: {
      axisBorder: {
        color: theme.palette.divider,
        show: true,
      },
      axisTicks: {
        color: theme.palette.divider,
        show: true,
      },
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      labels: {
        offsetY: 5,
        style: {
          colors: theme.palette.text.secondary,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => (value > 0 ? `${value}` : `${value}`),
        offsetX: -10,
        style: {
          colors: theme.palette.text.secondary,
        },
      },
    },
  };
};

const NocUsage = () => {
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const theme = useTheme();
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [nocYear, setNocYear] = useState(String(new Date().getFullYear()));
  const [nocYears, setNocYears] = useState([nocYear]);
  const [nocUsageTitle, setNocUsageTitle] = useState(false);
  const [isNocUsageFadingOut, setIsNocUsageFadingOut] = useState(false);
  const chartBase = useMemo(() => [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], []);
  const [nocChartList, setNocChartList] = useState(chartBase);

  const { nocUsage: uAnalyticsNocUsage } = useSelector(
    (state) => state[currentSession]?.user?.user?.analytics || {},
    shallowEqual
  );

  const { nocUsage: subUnocUsage } = useSelector(
    (state) => state[currentSession]?.user?.subuser || {},
    shallowEqual
  );

  const chartOptions = useMemo(
    () => getChartOptions(theme, mobileSize),
    [theme, mobileSize]
  );

  const handleChange = (event) => {
    setNocYear(event.target.value);
    handleNocChartValues();
  };

  const title =
    "This chart shows an overview of Notice of Commencement usage history.";

  const handleNocChartValues = useCallback(() => {
    let nocChart = [...chartBase];

    if (currentSession === "userSession") {
      uAnalyticsNocUsage?.forEach((item) => {
        if (item.year === nocYear) {
          nocChart[item.month] = item.noc;
        }
      });
    } else if (currentSession === "subUserSession") {
      subUnocUsage?.forEach((item) => {
        if (item.year === nocYear) {
          nocChart[item.month] = item.noc;
        }
      });
    }

    // ✅ Only set state if changed
    if (JSON.stringify(nocChartList) !== JSON.stringify(nocChart)) {
      setNocChartList(nocChart);
    }
  }, [
    chartBase,
    currentSession,
    nocYear,
    subUnocUsage,
    uAnalyticsNocUsage,
    nocChartList,
  ]);

  useEffect(() => {
    handleNocChartValues();
  }, [handleNocChartValues]);

  useEffect(() => {
    if (currentSession === "userSession") {
      if (uAnalyticsNocUsage) {
        const nocYearsSet = new Set();

        // Loop through each item and add its year to the Set
        uAnalyticsNocUsage?.forEach((item) => {
          nocYearsSet.add(item.year);
        });

        // Convert Set to array, sort it numerically, and update state
        setNocYears([...nocYearsSet].sort((a, b) => a - b));
      }
    } else if (currentSession === "subUserSession") {
      if (subUnocUsage) {
        const nocYearsSet = new Set();

        // Loop through each item and add its year to the Set
        subUnocUsage.forEach((item) => {
          nocYearsSet.add(item.year);
        });

        // Convert Set to array, sort it numerically, and update state
        setNocYears([...nocYearsSet].sort((a, b) => a - b));
      }
    }
  }, [currentSession, subUnocUsage, uAnalyticsNocUsage]);

  useEffect(() => {
    if (nocUsageTitle) {
      const nocUsageTimeout = setTimeout(() => {
        setIsNocUsageFadingOut(true);
        const nocUsageFadeOutTimeout = setTimeout(() => {
          setNocUsageTitle(false);
          setIsNocUsageFadingOut(false);
        }, 1000);

        return () => clearTimeout(nocUsageFadeOutTimeout);
      }, 3500);

      return () => clearTimeout(nocUsageTimeout);
    }
  }, [nocUsageTitle]);

  return (
    <Card sx={{ height: "100%" }} style={{ position: "relative" }}>
      {nocUsageTitle && (
        <SigneeRegisterInfo
          infoText={title}
          isFadingOut={isNocUsageFadingOut}
          top={35}
        />
      )}

      <CardHeader
        title="Notice of Commencement Usage"
        sx={{
          pb: "10px",
          pt: mobileSize ? "12px" : "20px",
          px: mobileSize ? "12px" : "24px",
        }}
        action={
          tabletSize ? (
            <IconButton onClick={() => setNocUsageTitle(!nocUsageTitle)}>
              <HelpOutlineIcon color="primary" />
            </IconButton>
          ) : (
            <Tooltip title={title} placement="top">
              <HelpOutlineIcon color="primary" />
            </Tooltip>
          )
        }
      />
      <CardContent
        sx={{
          px: mobileSize ? 0 : undefined,
          pt: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            width: "100%",
          }}
        >
          {nocYears?.length > 0 && (
            <FormControl
              fullWidth
              variant="filled"
              sx={{
                width: mobileSize ? "9rem" : "8rem",
                mr: mobileSize ? 1.5 : 0,
              }}
            >
              <InputLabel>Select year</InputLabel>
              <Select value={nocYear} label="select" onChange={handleChange}>
                {nocYears.map(function (item, i) {
                  return (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        </Box>
        <Chart
          height={350}
          options={chartOptions}
          series={[
            {
              name: "Created NOCs",
              data: nocChartList,
            },
          ]}
          type="bar"
          width="100%"
        />
      </CardContent>
    </Card>
  );
};

export default React.memo(NocUsage);
